import type { ExecutionStepProto } from "build-ts/chat_session";
import { ExecutionStatusType } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";

export type ToolExecutionRun = {
	steps: ExecutionStepProto[];
	/** True when the last step is TOOL_CALL_SUCCESS */
	completed: boolean;
	/** True when the last step is TOOL_CALL_FAILURE or ERROR_STATE */
	failed: boolean;
};

function isTerminalStep(status: ExecutionStatusType | undefined): boolean {
	if (status === undefined) return false;
	return (
		status === ExecutionStatusType.TOOL_CALL_SUCCESS ||
		status === ExecutionStatusType.TOOL_CALL_FAILURE ||
		status === ExecutionStatusType.ERROR_STATE
	);
}

/**
 * One UI row per tool invocation: starts at EXECUTING_TOOL_CALL, ends at
 * TOOL_CALL_SUCCESS / TOOL_CALL_FAILURE / ERROR_STATE. Intermediate statuses
 * update the same row's label.
 *
 * `EXECUTING_LLM_CALL` is omitted: it is not stored on live messages in `Chat.handleExecutionStep`
 * (the main loader already shows "Thinking…"), but persisted history can still contain it.
 */
export function groupExecutionStepsByToolRun(steps: ExecutionStepProto[] | undefined): ToolExecutionRun[] {
	if (!steps?.length) return [];

	const timelineSteps = steps.filter(s => s.status !== ExecutionStatusType.EXECUTING_LLM_CALL);
	if (!timelineSteps.length) return [];

	const groups: ToolExecutionRun[] = [];
	let current: ExecutionStepProto[] | null = null;

	for (const step of timelineSteps) {
		const st = step.status;

		if (st === ExecutionStatusType.EXECUTING_TOOL_CALL) {
			if (current) {
				groups.push({ steps: current, completed: false, failed: false });
			}
			current = [step];
			continue;
		}

		if (!current) continue;

		current.push(step);
		if (isTerminalStep(st)) {
			const failed = st === ExecutionStatusType.TOOL_CALL_FAILURE || st === ExecutionStatusType.ERROR_STATE;
			const completed = st === ExecutionStatusType.TOOL_CALL_SUCCESS;
			groups.push({ steps: current, completed, failed });
			current = null;
		}
	}

	if (current) {
		groups.push({ steps: current, completed: false, failed: false });
	}
	return groups;
}

export type ToolRunStreamIconKind = "search" | "analysis" | "dataframe";

export function findToolType(steps: ExecutionStepProto[]): DarwinToolType | undefined {
	for (const s of steps) {
		if (s.tool_type !== undefined && s.tool_type !== null) return s.tool_type;
	}
	return undefined;
}

/** Icon for the live timeline: search vs analysis vs dataframe tooling. */
export function toolRunStreamIconKind(run: ToolExecutionRun): ToolRunStreamIconKind {
	const tool = findToolType(run.steps);
	switch (tool) {
		case DarwinToolType.DATAFRAME_TOOL:
		case DarwinToolType.KG_OBSERVATION_DATA:
			return "dataframe";
		case DarwinToolType.GET_PAST_JOB_RUNS:
		case DarwinToolType.BUILD_ANALYSIS_OUTPUT_TOOL:
			return "search";
		default:
			return "analysis";
	}
}
